import { ErrorBoundary } from "@sentry/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw, TriangleAlert, X } from "lucide-react";
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { LoadErrorCard, LoadingCard } from "@/components/load-state";
import { PAGE_GUTTER } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  type ApiError,
  APPS_KEY,
  type AppSnapshotInfo,
  BEZETTING_SNAPSHOT_KEY,
  useBezettingMeldingen,
  useBezettingSnapshot,
} from "@/lib/api";
import { apiErrorCode, apiErrorMessage, isToastedError } from "@/lib/api-errors";
import { formatPercent, formatTime } from "@/lib/format";
import { i18n } from "@/lib/i18n";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

import { AppAccessDenied } from "../access-denied";
import type {
  BezettingSectionLoader,
  BezettingSnapshotRef,
  BezettingTab,
  RecomputeHook,
} from "./contract";
import { jobErrorText } from "./job-errors";
import { RecomputeController, useRecomputeState } from "./recompute";
import { RecomputeDialog } from "./recompute-dialog";
import { wholePercent } from "./recompute-progress";
import { createSectionLoader, namesOf, snapshotRef } from "./sections";

export { jobErrorText } from "./job-errors";

// The renderer and d3 load with the app's first visit, not with the dashboard.
const BezettingMount = lazy(() =>
  import("./mount").then((module) => ({ default: module.BezettingMount })),
);

/** A message on the page's banner, worded at render so a language switch re-words it. */
export interface BannerItem {
  id: number;
  text: () => string;
}

/** How many messages the banner keeps: the latest ones. */
export const BANNER_MAX = 3;

/** No client names: the snapshot has no `namen` section, or it could not be read. */
const NO_NAMES: ReadonlyMap<number, string> = new Map();

/**
 * Whether a failure is the server refusing the app to this person. A write
 * refused because a developer views the dashboard as someone else is a 403
 * too, but the app is still theirs to read: a toast says that one.
 */
export function isAppDenied(error: unknown): boolean {
  return (
    (error as Partial<ApiError> | null)?.status === 403 &&
    apiErrorCode(error) !== "view_as_read_only"
  );
}

/**
 * A renderer error the reader need not see: the snapshot was replaced (the
 * loader moves the page onto the newest), the app was refused (the host
 * shows the denied page), or a toast already says it (`isToastedError`).
 */
export function isHandledRendererError(error: unknown): boolean {
  return (
    apiErrorCode(error) === "snapshot_not_found" || isAppDenied(error) || isToastedError(error)
  );
}

/** What the renderer reports: a request that failed, else a part of the page it could not draw. */
export function rendererErrorText(error: unknown): string {
  const status = (error as Partial<ApiError> | null)?.status;
  return typeof status === "number" ? apiErrorMessage(error, "appData") : i18n.t("apps.pageError");
}

interface BezettingHostProps {
  tab: BezettingTab;
}

/**
 * The Bezettingsgraad page: its title, a banner for what went wrong, and the
 * renderer, which draws the app's own header strip (the Odoo-stand, the
 * unread meldingen, Herbereken), its tabs and the page. Before the first
 * recompute the page offers Herbereken itself; to someone who may not open
 * the app it says so, and who to ask. Herbereken's progress shows in a
 * dialog over the page. Full width up to 1440 px, as the app was built for
 * wide screens.
 */
export function BezettingHost({ tab }: BezettingHostProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const summary = useBezettingSnapshot();
  const [banner, setBanner] = useState<BannerItem[]>([]);
  const [dismissedJob, setDismissedJob] = useState<string | null>(null);
  const report = useCallback((text: () => string) => {
    setBanner((items) => [
      ...items.slice(1 - BANNER_MAX),
      { id: Date.now() + Math.random(), text },
    ]);
  }, []);
  // One for the page: the empty state's button and the renderer's share it.
  const [recompute] = useState(
    () =>
      new RecomputeController({
        queryClient,
        // A refusal while viewing as someone else is said in a toast, not in the banner too.
        onFailure: (error) => {
          if (!isToastedError(error)) {
            report(() => apiErrorMessage(error, "recompute"));
          }
        },
      }),
  );
  useEffect(() => {
    recompute.attach();
    return () => recompute.detach();
  }, [recompute]);

  // Access taken away while the page was open: the sidebar drops the app too.
  const denied = isAppDenied(summary.error);
  useEffect(() => {
    if (denied) {
      void queryClient.invalidateQueries({ queryKey: APPS_KEY, exact: true });
    }
  }, [denied, queryClient]);

  const title = t("apps.bezetting.title");
  const data = summary.data;
  const failedJob =
    data?.job?.status === "failed" && data.job.id !== dismissedJob ? data.job : null;

  let content;
  if (isAppDenied(summary.error)) {
    logger.debug("[bezetting]", "no access");
    content = <AppAccessDenied />;
  } else if (data) {
    content = data.snapshot ? (
      <BezettingView info={data.snapshot} tab={tab} recompute={recompute} report={report} />
    ) : (
      <BezettingEmpty recompute={recompute} />
    );
  } else if (summary.isError) {
    content = (
      <LoadErrorCard
        message={t("apps.loadError", { app: title })}
        onRetry={() => void summary.refetch()}
      />
    );
  } else {
    content = <LoadingCard />;
  }

  return (
    <div className={cn("mx-auto w-full max-w-[1440px] py-8", PAGE_GUTTER)}>
      <h1 className="font-heading text-primary mb-6 text-2xl font-bold">{title}</h1>
      <Banner
        items={[
          ...(failedJob && !isAppDenied(summary.error)
            ? [
                {
                  id: -1,
                  text: () => jobErrorText(failedJob, Boolean(data?.snapshot)),
                  onDismiss: () => setDismissedJob(failedJob.id),
                },
              ]
            : []),
          ...banner.map((item) => ({
            ...item,
            onDismiss: () => setBanner((items) => items.filter((other) => other.id !== item.id)),
          })),
        ]}
      />
      {content}
      {!denied && <RecomputeDialog recompute={recompute} summary={data} />}
    </div>
  );
}

interface BezettingViewProps {
  info: AppSnapshotInfo;
  tab: BezettingTab;
  recompute: RecomputeHook;
  report: (text: () => string) => void;
}

/** What the renderer gets for one snapshot: the snapshot, its sections and its client names. */
interface Shown {
  snapshot: BezettingSnapshotRef;
  sections: BezettingSectionLoader;
  names: ReadonlyMap<number, string>;
}

/**
 * The renderer on the latest snapshot. It mounts once the client names and
 * the meldingen are in (or could not be read). A newer snapshot takes over
 * only once its own names are in, so the page stays drawn meanwhile and then
 * moves onto it in place.
 */
function BezettingView({ info, tab, recompute, report }: BezettingViewProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { search } = useLocation();
  const snapshot = useMemo(() => snapshotRef(info), [info]);
  // Access taken away while the page is open: the summary answers 403 now, and the
  // host shows the denied page.
  const accessLost = useCallback(
    () => void queryClient.invalidateQueries({ queryKey: BEZETTING_SNAPSHOT_KEY, exact: true }),
    [queryClient],
  );
  const sections = useMemo(
    () =>
      createSectionLoader(queryClient, snapshot, {
        // A pruned snapshot: the summary names the newest, which this view then shows.
        onSnapshotGone: () =>
          void queryClient.invalidateQueries({ queryKey: BEZETTING_SNAPSHOT_KEY, exact: true }),
        onDenied: accessLost,
      }),
    [queryClient, snapshot, accessLost],
  );
  const namesQuery = useQuery({
    queryKey: ["apps", "bezetting", "names", snapshot.id],
    queryFn: async () => namesOf(await sections.tryLoad("namen")),
    staleTime: Infinity,
  });
  const meldingenQuery = useBezettingMeldingen();

  const [shown, setShown] = useState<Shown | null>(null);
  const ready = !namesQuery.isPending && !meldingenQuery.isPending;
  if (ready && shown?.snapshot.id !== snapshot.id) {
    if (namesQuery.isError) {
      logger.warn("[bezetting]", "client names unavailable");
    }
    setShown({ snapshot, sections, names: namesQuery.data ?? NO_NAMES });
  }

  const onError = useCallback(
    (error: unknown) => {
      if (isHandledRendererError(error)) {
        return;
      }
      logger.error("[bezetting]", "renderer", error instanceof Error ? error.name : typeof error);
      report(() => rendererErrorText(error));
    },
    [report],
  );
  const onSaveError = useCallback(
    (error: unknown) => {
      if (isAppDenied(error)) {
        accessLost();
        return;
      }
      if (!isToastedError(error)) {
        report(() => apiErrorMessage(error, "saveMeldingen"));
      }
    },
    [report, accessLost],
  );

  if (!shown) {
    return <LoadingCard />;
  }
  return (
    <>
      {meldingenQuery.isError && (
        <p className="text-muted mb-4 text-sm">{t("apps.meldingenError")}</p>
      )}
      {/* A renderer that fails to load (its file replaced by a release) or to draw stays
          inside the page: the sidebar and the rest of the dashboard keep working. */}
      <ErrorBoundary
        fallback={
          <LoadErrorCard
            message={t("apps.loadError", { app: t("apps.bezetting.title") })}
            onRetry={() => window.location.reload()}
          />
        }
      >
        <Suspense fallback={<LoadingCard />}>
          <BezettingMount
            tab={tab}
            search={search.replace(/^\?/, "")}
            snapshot={shown.snapshot}
            sections={shown.sections}
            names={shown.names}
            recompute={recompute}
            onError={onError}
            onSaveError={onSaveError}
          />
        </Suspense>
      </ErrorBoundary>
    </>
  );
}

interface BezettingEmptyProps {
  recompute: RecomputeHook;
}

/** Before the first recompute: what Herbereken does, and the button. */
export function BezettingEmpty({ recompute }: BezettingEmptyProps) {
  const { t } = useTranslation();
  return <AppEmpty recompute={recompute} body={t("apps.empty.body")} />;
}

interface AppEmptyProps {
  recompute: RecomputeHook;
  /** What Herbereken computes for this app, in the dashboard's language. */
  body: string;
  /** The heading, "Nog geen gegevens" by default. */
  title?: string;
}

/** An app before its first recompute: "Nog geen gegevens", what Herbereken does, and the button. */
export function AppEmpty({ recompute, body, title }: AppEmptyProps) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
        <h2 className="font-heading text-primary text-lg font-bold">
          {title ?? t("apps.empty.title")}
        </h2>
        <p className="text-muted max-w-prose text-sm">{body}</p>
        <RecomputeButton recompute={recompute} />
      </CardContent>
    </Card>
  );
}

/**
 * Herbereken as a dashboard button, with what stops it now under it. While a
 * recompute runs it says how far it is, and a click shows its progress.
 */
export function RecomputeButton({ recompute }: { recompute: RecomputeHook }) {
  const { t } = useTranslation();
  const state = useRecomputeState(recompute);
  const running = state.kind === "running";
  const showsProgress = running && typeof recompute.showProgress === "function";
  const note =
    state.kind === "waiting"
      ? t("apps.recompute.waiting", { time: formatTime(state.until) })
      : state.kind === "unavailable"
        ? t("apps.recompute.unavailable")
        : null;
  return (
    <div className="flex flex-col items-center gap-2">
      <Button
        onClick={() => (running ? recompute.showProgress?.() : recompute.start())}
        disabled={state.kind !== "idle" && !showsProgress}
        aria-busy={running}
        className="tabular-nums"
      >
        {running ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
        )}
        {!running
          ? t("apps.recompute.start")
          : state.fraction === null
            ? t("apps.recompute.running")
            : t("apps.recompute.runningPercent", {
                percent: formatPercent(wholePercent(state.fraction) / 100),
              })}
      </Button>
      {note && <p className="text-muted text-xs">{note}</p>}
    </div>
  );
}

interface BannerProps {
  items: readonly (BannerItem & { onDismiss: () => void })[];
}

/** What went wrong on the page, newest last, each with a close button. */
export function Banner({ items }: BannerProps) {
  const { t } = useTranslation();
  return (
    <div role="alert" aria-live="assertive" className={cn(items.length > 0 && "mb-4 space-y-2")}>
      {items.map((item) => (
        <div
          key={item.id}
          className="border-destructive/30 bg-destructive/10 text-foreground flex items-start gap-2 rounded-md border px-4 py-3 text-sm"
        >
          <TriangleAlert aria-hidden="true" className="text-destructive mt-0.5 size-4 shrink-0" />
          <span className="min-w-0 flex-1">{item.text()}</span>
          <button
            type="button"
            onClick={item.onDismiss}
            aria-label={t("common.close")}
            className="text-muted hover:text-foreground focus-visible:ring-ring tap-target -mr-1 rounded-sm p-0.5 focus-visible:ring-2 focus-visible:outline-none pointer-coarse:relative"
          >
            <X aria-hidden="true" className="size-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
